import { store } from '@redux/store';
import getTrackingCommonProperties from 'features/trackingProperties';
import merge from 'deepmerge';
import cleanObjectsEmptyProperties from 'utils/cleanObject';
import GoogleAnalytics4EventType from 'enums/googleAnalytics4EventType';
import GoogleAnalytics4Event from 'dataTransferObjects/googleAnalytics4Event';
import getCentralStandardTime from 'modules/getCentralStandardTime';
import { LocationType } from 'features/searchResultsFacets';

/**
 * The getCommonProperties function returns an object containing the common properties
 * that are used in all Google Analytics 4 events.
 * @return An object
 */
const getCommonProperties = () => {
  const state = store.getState();
  const commonProperties = getTrackingCommonProperties(state);
  return commonProperties;
};

/**
 * concatenates a market name and a state abbreviation with a comma in between.
 * @param marketName - is a string representing the name of a market.
 * @param stateAbbreviation - represents a state. For example, "CA" for California or "NY" for New York.
 * @returns a string that concatenates the marketName and stateAbbreviation
 * with a comma and space in between. If `marketName` is falsy, the function returns `null`.
 */
const getMarketName = (marketName, stateAbbreviation) => {
  if (marketName) {
    return `${marketName}, ${stateAbbreviation}`;
  }
  return null;
};

/**
 * It takes in a market ID and market name as parameters and returns a
 * string that combines the market name and ID.
 * @param marketId - is used to identify a specific market. It could be a unique
 * identifier or a code that represents the market.
 * @param marketName - is a string that represents the name of a market.
 * @param stateAbbreviation - is a string that represents the abbreviation name of the state.
 * @returns a string that concatenates the `marketName` and
 * `marketId` with a hyphen in between. If both `marketId` and `marketName` are provided, the function
 * returns the concatenated string. Otherwise, it returns an empty string.
 */
export const getMarketSession = (marketId, marketName, stateAbbreviation) => {
  if (marketId && marketName) {
    return `${marketName}, ${stateAbbreviation} - ${marketId}`;
  }
  return null;
};

/**
 * The getLocation function returns a string that represents the location, including the city and state
 * name if provided.
 * @param city - The `city` parameter represents the name of a city.
 * @param stateName - The `stateName` parameter represents the name of a state.
 * @returns If the `city` parameter is provided, the function will return a string in the format "city,
 * stateName". If the `city` parameter is not provided, the function will return just the `stateName`.
 */
export const getLocation = (city, stateName) => {
  if (city) {
    return `${city}, ${stateName}`;
  }
  return stateName;
};

/**
 * Get Location type and returns a string that represents the subpage, concept introduced for GA events.
 * @param locationType - The `location type` parameter represents the name of the location type.
 * @returns Returns the location type if it matches Neighborhood or School District..
 */
const getSubPageType = (locationType) => {
  if (locationType === LocationType.Neighborhood.toString() || 
      locationType === LocationType.SchoolDistrict.toString()) {
    return locationType;
  }
  return null;
}

/**
 * The function filters the Google Analytics 4 common properties, and take only the ones needed for Track events.
 * ex. track don't need user information. user information is available in common properties.
 * @returns object containing the common properties for a Track event.
 */
const getTrackCommonProperties = () => {
  const commonProperties = getCommonProperties();

  const trackCommonProperties = {
    brandPartnerName: commonProperties.brandPartnerName,
    pageType: commonProperties.pageType,
    sub_page_type: getSubPageType(commonProperties.locationType),
    partnerId: commonProperties.partnerId,
    partnerName: commonProperties.partnerName,
    userMarketId: commonProperties.userLocationMarketId,
    userStatus: commonProperties.isLoggedIn ? 1 : 0,
    userZipCode: commonProperties.userLocationZipCode,
    user: commonProperties.userId,
    brandPartnerNamePartnerName: `${commonProperties.brandPartnerName}-${commonProperties.partnerName}`,
    gaUserId: commonProperties.userId,
    time: getCentralStandardTime(),
    neustarResponse: JSON.stringify({
      s: commonProperties.neustarSegment,
      z: commonProperties.neustarZip,
      a: commonProperties.neustarAge,
      g: commonProperties.neustarGender,
    }),
    referCode: commonProperties.referCode,
    market_id: commonProperties.marketId?.toString() || null,
    market_name: getMarketName(commonProperties.marketName, commonProperties.stateAbbreviation),
    market_session: getMarketSession(
      commonProperties.marketId?.toString(),
      commonProperties.marketName,
      commonProperties.stateAbbreviation
    ),
    location: getLocation(commonProperties.city, commonProperties.stateName),
    current_page_number: commonProperties.pageNumber || null,
    search_within_map: commonProperties.isSearchWithinMap || null,
    page_number: commonProperties.pageNumber || null,
  };

  const cleanedTrackCommonProperties = cleanObjectsEmptyProperties(trackCommonProperties);

  return cleanedTrackCommonProperties;
};

/**
 * The createTrackEvent function is a helper function that creates an event object
 * for the Google Analytics 4. The event object contains information about
 * the type of event, and any properties associated with it. This function returns
 * a redux-beacon middleware compatible action creator that can be dispatched to
 * trigger an analytics call to Google Analytics 4's servers.
 * @param eventName Identify the event name
 * @param customParams Pass in any additional properties that you want to be sent with the event
 *
 * @return A function that returns a GoogleAnalytics4Event
 */
const createTrackEvent = (eventName, customParams) => {
  const commonProperties = getTrackCommonProperties();
  const properties = merge(commonProperties, customParams);

  return () => new GoogleAnalytics4Event(eventName, properties);
};

/**
 * The getGoogleAnalytics4Event function returns a GoogleAnalytics4Event object.
 * @param eventType Determine which type of event to create
 * @param eventName Identify the event name
 * @param customParams Send custom parameters to the event.
 *
 * @return An GoogleAnalytics4Event object
 */
export const getGoogleAnalytics4Event = (eventType, eventName, customParams) => {
  if (eventType === GoogleAnalytics4EventType.Event) {
    return createTrackEvent(eventName, customParams);
  }

  return null;
};
